// src/pages/admin/AdminInbox.jsx
import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const AdminInbox = () => {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [replies, setReplies] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [adminId, setAdminId] = useState(null);

  useEffect(() => {
    getAdminId();
    fetchMessages();
  }, [filter]);

  const getAdminId = async () => {
    const { data } = await supabase.auth.getSession();
    setAdminId(data?.session?.user?.id);
  };

  const fetchMessages = async () => {
    let query = supabase
      .from('user_to_admin_messages')
      .select('*, user:registrations(user_id, full_name, country, age_range)')
      .order('created_at', { ascending: false });

    if (filter === 'handled') query = query.eq('is_handled', true);
    if (filter === 'unhandled') query = query.eq('is_handled', false);

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching messages:', error);
    } else {
      setMessages(data);
      fetchReplies(data.map((m) => m.id));
    }
  };

  const fetchReplies = async (messageIds) => {
    const { data, error } = await supabase
      .from('message_replies')
      .select('*')
      .in('user_to_admin_message_id', messageIds);

    if (!error) {
      const grouped = data.reduce((acc, r) => {
        if (!acc[r.user_to_admin_message_id]) acc[r.user_to_admin_message_id] = [];
        acc[r.user_to_admin_message_id].push(r);
        return acc;
      }, {});
      setReplies(grouped);
    }
  };

  const markAsHandled = async (id) => {
    const { error } = await supabase
      .from('user_to_admin_messages')
      .update({ is_handled: true })
      .eq('id', id);

    if (!error) {
      toast.success('Marked as handled');
      fetchMessages();
    } else {
      toast.error('Error updating message');
    }
  };

  const handleAdminReply = async (msgId) => {
    const replyText = replyInputs[msgId];
    if (!replyText || !adminId) return toast.error('Reply cannot be empty');

    const { error } = await supabase.from('message_replies').insert({
      user_to_admin_message_id: msgId,
      sender_id: adminId,
      body: replyText,
    });

    if (error) {
      toast.error('Failed to send reply');
    } else {
      toast.success('Reply sent');
      setReplyInputs((prev) => ({ ...prev, [msgId]: '' }));
      fetchReplies(messages.map((m) => m.id));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">📥 Admin Inbox</h2>

      {/* Filter */}
      <div className="flex gap-4 mb-4">
        {['all', 'unhandled', 'handled'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded border font-medium ${
              filter === f ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 border-gray-300'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Messages */}
      {messages.length === 0 ? (
        <p>No messages found.</p>
      ) : (
        messages.map(({ id, subject, body, created_at, is_handled, user }) => {
          const threadReplies = replies[id] || [];
          const hasUnreadUserReply = threadReplies.some(
            (r) => r.sender_id !== adminId
          );

          return (
            <div key={id} className="border rounded p-4 mb-6 bg-white shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {subject}
                  {hasUnreadUserReply && (
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </h3>
                {!is_handled && (
                  <button
                    onClick={() => markAsHandled(id)}
                    className="text-sm bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700"
                  >
                    Mark as Handled
                  </button>
                )}
              </div>

              <p className="text-sm text-gray-500 mb-2">
                From: {user?.full_name || 'Unknown'} | {user?.country} | {user?.age_range} <br />
                Sent: {format(new Date(created_at), 'PPpp')}
              </p>

              <p className="text-gray-800 whitespace-pre-line">{body}</p>

              {is_handled && <p className="text-xs text-green-600 mt-2">✔ Marked as handled</p>}

              {/* Replies Thread */}
              {threadReplies.length > 0 && (
                <div className="mt-4 border-t pt-3">
                  <h4 className="text-sm font-semibold mb-2">Threaded Replies</h4>
                  {threadReplies.map((reply) => (
                    <div key={reply.id} className="mb-2 text-sm">
                      <p className="text-xs text-gray-500">
                        {reply.sender_id === adminId ? '🛡️ Admin' : '👤 Learner'} • {format(new Date(reply.created_at), 'PPpp')}
                      </p>
                      <p className="text-gray-700">{reply.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Admin Reply Field */}
              <div className="mt-4">
                <textarea
                  rows={2}
                  placeholder="Write a reply..."
                  value={replyInputs[id] || ''}
                  onChange={(e) =>
                    setReplyInputs((prev) => ({ ...prev, [id]: e.target.value }))
                  }
                  className="w-full border rounded p-2 text-sm"
                />
                <button
                  onClick={() => handleAdminReply(id)}
                  className="mt-2 bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                >
                  Send Reply
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default AdminInbox;
